import { api } from '../api'

const LINKEDIN_ACCOUNT = "linkedin"
const WORKATSTARTUP_ACCOUNT = "work_at_startup"

/**
 * User data layer
 *
 * Provides methods to read and manipulate user data
 * Depends on an API module that provides HTTP methods
 *
 * @param {object} api - API module to interact with the server
 * @param object.get - GET method
 * @param object.post - POST method
 * @param object.put - POST method
 */
const UsersAPI = () => {
    return {
        getAllUsers,
        createUser,
        deActivateUser,
        activateUser,
        createAuthAccount,
        loadAuthAccounts
    }

    /**
     * Get a list of all users
     */
    async function getAllUsers() {
        const data = await api.get('/api/scrapery/users')

        return data.map(u => ({ ...u, key: u.id }))
    }

    /**
     * Create a new user
     * @param {object} userDetails information about the user
     * @returns error or successful user object
     */
    async function createUser(data) {
        const url = '/api/admin/register'

        return await api.post(url, data)
    }

    /**
     * Activate a user with the given user id
     * @param {number} userId - id of the user
     */
    async function activateUser(userId) {
        const url = `/api/admin/${userId}/active`

        return await api.put(url)
    }

    /**
     * De activate a user with the given user id
     * @param {number} userId - id of the user
     */
    async function deActivateUser(userId) {
        const url = `/api/admin/${userId}/inactive`

        return await api.put(url)
    }

    /**
     * Get all registered linkedin accounts
     */
    async function loadLinkedinAccounts() {
        const url = `/api/scrapery/linkedin_accounts`

        const accounts = await api.get(url)

        return accounts.map(account => ({ ...account, provider: 'Linkedin', key: account.id }))
    }

    /**
   * Get all registered work at startup accounts
   */
    async function loadWorkAtAStartupAccounts() {
        const url = `/api/scrapery/work_at_a_startup_accounts`

        const accounts = await api.get(url)

        return accounts.map(account => ({ ...account, provider: account.provider, key: account.id }))
    }


    /**
     * Load all registered auth accounts
     */
    async function loadAuthAccounts() {
        const allAccounts = [
            await loadLinkedinAccounts(),
            await loadWorkAtAStartupAccounts()
        ]

        try {
            const [...accounts] = await Promise.all(allAccounts)
            return accounts.flat()
        } catch (error) {
            throw error
        }
    }

    /**
     * Create a new linkedin account
     */
    async function createLinkedinAccount(data) {
        const url = `/api/scrapery/linkedin_accounts`

        return await api.post(url, { ...data, active: true })
    }


    /**
     * Create a new linkedin account
     */
    async function createWorkAtAStartupAccount(data) {
        const url = `/api/scrapery/work_at_a_startup_accounts`

        return await api.post(url, { ...data, active: true })
    }


    /**
     * API to create an auth account
     * 
     * @param {object} data - account data username, passwords ...
     * @returns success/failure
     */
    async function createAuthAccount(data) {
        // create account based on kind
        const { provider } = data
        switch (provider) {
            case LINKEDIN_ACCOUNT:
                return await createLinkedinAccount(data)
            case WORKATSTARTUP_ACCOUNT:
                return await createWorkAtAStartupAccount(data)
            default:
                throw Error(`Handler function to create account for ${provider} is not found.`)

        }
    }


}

export default UsersAPI
